import { Component, Input, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { FormGroup, FormControl, AbstractControl, ReactiveFormsModule } from '@angular/forms';

import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-typeahead';
import { RepairPartService } from '../../../services/repair-part.service';
import { CodeDescription } from '../../../models/code-description.model';
import { IMcTypeaheadData } from '@maersk-global/mds-components-core/mc-typeahead/types';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-repair-part-information',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './repair-part-information.component.html',
  styleUrls: ['./repair-part-information.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairPartInformationComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() isEditMode: boolean = false;

  repairModeList: CodeDescription[] = [];
  repairCodeTypeaheadData: IMcTypeaheadData[] = [];
  partCodeTypeaheadData: IMcTypeaheadData[] = [];
  isLoadingModes = false;
  isLoadingCodes = false;
  isLoadingParts = false;

  constructor(private readonly repairPartService: RepairPartService) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  private async initializeComponent(): Promise<void> {
    await this.loadModes();
    if (!this.isEditMode) {
      await this.loadParts();
    }
  }

  private async loadModes(): Promise<void> {
    this.isLoadingModes = true;
    try {
      const modes = await firstValueFrom(this.repairPartService.getAllRepairModes());
      this.repairModeList = modes || [];
    } catch (err) {
      console.error('Error loading modes:', err);
      this.repairModeList = [];
    } finally {
      this.isLoadingModes = false;
    }
  }

  async onModeChange(event: Event): Promise<void> {
    const customEvent = event as CustomEvent;
    const selectedMode = customEvent?.detail?.value ?? customEvent?.detail;
    if (selectedMode) {
      this.formGroup.get('repairCode')?.setValue(null);
      await this.loadRepairCodes(selectedMode);
    }
  }

  private async loadRepairCodes(repairMode: string): Promise<void> {
    this.isLoadingCodes = true;
    this.repairCodeTypeaheadData = [];
    try {
      const codes = await firstValueFrom(this.repairPartService.getAllRepairCodes(repairMode));
      this.repairCodeTypeaheadData = (codes || []).map((c) => ({
        label: c.code,
        sublabel: c.description,
        value: c.code,
      }));
    } catch (err) {
      console.error('Error loading repair codes:', err);
    } finally {
      this.isLoadingCodes = false;
    }
  }

  private async loadParts(): Promise<void> {
    this.isLoadingParts = true;
    try {
      const parts = await firstValueFrom(this.repairPartService.getAllPartsWithDescription());
      this.partCodeTypeaheadData = (parts || []).map((p) => ({
        label: p.code,
        sublabel: p.description || '',
        value: p.code,
      }));
    } catch (err) {
      console.error('Error loading parts:', err);
    } finally {
      this.isLoadingParts = false;
    }
  }

  isControlInErrorState(control: FormControl | AbstractControl | null | undefined): boolean | null {
    if (control?.invalid && control.touched) {
      return true;
    }
    return null;
  }
}
